import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle, ArrowRight, Compass, Package } from "@phosphor-icons/react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [data, setData] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= 15) {
        setStatus("timeout");
        return;
      }
      attemptsRef.current += 1;
      try {
        const { data: d } = await api.get(`/checkout/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (d.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <>
          <div className="w-20 h-20 border border-[#FFD700] rotate-45 mx-auto mb-8 animate-pulse-gold flex items-center justify-center">
            <Package size={32} weight="duotone" className="text-[#FFD700] -rotate-45" />
          </div>
          <span className="overline">// Verifying Provision</span>
          <h1 className="heading-display text-4xl mt-3 mb-4">Securing your provision...</h1>
          <p className="text-white/60 font-light">Confirming with the Stripe bridge.</p>
        </>
      )}

      {status === "paid" && (
        <>
          <CheckCircle size={80} weight="duotone" className="text-[#FFD700] mx-auto mb-6 animate-pulse-gold" />
          <span className="overline">// Provision Secured</span>
          <h1 className="heading-display text-4xl md:text-5xl mt-3 mb-4">Welcome aboard, Commander.</h1>
          <p className="text-white/70 font-light mb-2">
            <span className="text-[#FFD700] font-semibold">{data?.product_title}</span> has been deposited in your vault.
          </p>
          <p className="font-mono text-xs uppercase tracking-widest text-white/40 mb-10">
            ${(data?.amount_total / 100).toFixed(2)} {data?.currency?.toUpperCase()}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/commander" data-testid="success-vault-btn" className="btn-gold px-8 py-4 text-xs inline-flex items-center gap-3">
              <Package size={16} /> Open My Vault
            </Link>
            <Link to="/blueprint" data-testid="success-blueprint-btn" className="btn-ghost px-8 py-4 text-xs inline-flex items-center gap-3">
              <Compass size={16} /> Commission a Blueprint <ArrowRight size={14} />
            </Link>
          </div>
        </>
      )}

      {(status === "expired" || status === "error" || status === "timeout") && (
        <>
          <span className="overline text-red-400">// Channel Disrupted</span>
          <h1 className="heading-display text-4xl mt-3 mb-4">Checkout incomplete</h1>
          <p className="text-white/60 font-light mb-8">
            {status === "expired" ? "Session expired." : "We couldn't verify the payment in time."}
          </p>
          <button onClick={() => navigate("/catalog")} className="btn-gold px-8 py-4 text-xs">
            Return to Arsenal
          </button>
        </>
      )}
    </div>
  );
}
